/* eslint-disable class-methods-use-this */
import { GlassEaselTemplateDOM } from './native_rendering';
import { ProcGenWrapper, } from './proc_gen_wrapper';
const DEFAULT_PROC_GEN = (R, C, D, U, A) => ({
    C: (isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot) => {
        defineSlot('');
    },
    B: (A || Object.create(null)),
});
export const DEFAULT_PROC_GEN_GROUP = () => DEFAULT_PROC_GEN;
export { GeneralLvaluePathPrefix } from './proc_gen_wrapper';
var BindingMapUpdateEnabled;
(function (BindingMapUpdateEnabled) {
    BindingMapUpdateEnabled[BindingMapUpdateEnabled["Disabled"] = 0] = "Disabled";
    BindingMapUpdateEnabled[BindingMapUpdateEnabled["Enabled"] = 1] = "Enabled";
    BindingMapUpdateEnabled[BindingMapUpdateEnabled["Forced"] = 2] = "Forced";
})(BindingMapUpdateEnabled || (BindingMapUpdateEnabled = {}));
// eslint-disable-next-line no-bitwise
const isPositiveInteger = (x) => String(x >>> 0) === x;
export class GlassEaselTemplateEngine {
    create(behavior, componentOptions) {
        if (componentOptions.externalComponent) {
            return new GlassEaselTemplateDOM(behavior);
        }
        return new GlassEaselTemplate(behavior);
    }
}
class GlassEaselTemplate {
    constructor(behavior) {
        this.updateTemplate(behavior);
    }
    /**
     * Update the underlying template content
     *
     * This method does not affect created instances.
     */
    updateTemplate(behavior) {
        const template = behavior._$template;
        if (typeof template !== 'object' && template !== undefined) {
            throw new Error(`Component template of ${behavior.is} must be a valid compiled template (or "null" for default template).`);
        }
        const c = template || {
            content: DEFAULT_PROC_GEN_GROUP,
        };
        this.genObjectGroupEnv = {
            list: c.groupList || Object.create(null),
            group: c.content,
        };
        this.updateMode = c.updateMode || '';
        this.fallbackListenerOnNativeNode = c.fallbackListenerOnNativeNode || false;
    }
    createInstance(comp, createShadowRoot) {
        return new GlassEaselTemplateInstance(this, comp, createShadowRoot(comp));
    }
}
export class GlassEaselTemplateInstance {
    constructor(template, comp, shadowRoot) {
        this.comp = comp;
        this.shadowRoot = shadowRoot;
        this.shadowRoot.destroyBackendElementOnRemoval();
        this._$applyTemplate(template);
    }
    updateTemplate(template, data) {
        this._$applyTemplate(template);
        this.shadowRoot.removeChildren(0, this.shadowRoot.childNodes.length);
        this.bindingMapGen = this.procGenWrapper.create(data);
    }
    _$applyTemplate(template) {
        const procGen = template.genObjectGroupEnv.group('') || DEFAULT_PROC_GEN_GROUP('');
        if (template.updateMode === 'bindingMap') {
            this.forceBindingMapUpdate = 2 /* BindingMapUpdateEnabled.Forced */;
        }
        else if (template.updateMode === 'virtualTree') {
            this.forceBindingMapUpdate = 0 /* BindingMapUpdateEnabled.Disabled */;
        }
        else {
            this.forceBindingMapUpdate = 1 /* BindingMapUpdateEnabled.Enabled */;
        }
        this.procGenWrapper = new ProcGenWrapper(this.shadowRoot, procGen, template.fallbackListenerOnNativeNode);
        this.bindingMapGen = undefined;
    }
    initValues(data) {
        this.bindingMapGen = this.procGenWrapper.create(data);
        return this.shadowRoot;
    }
    updateValues(data, changes) {
        if (this.forceBindingMapUpdate === 2 /* BindingMapUpdateEnabled.Forced */) {
            for (let i = 0; i < changes.length; i += 1) {
                this.tryBindingMapUpdate(data, changes[i]);
            }
            return;
        }
        if (this.forceBindingMapUpdate === 1 /* BindingMapUpdateEnabled.Enabled */ && changes.length <= 1) {
            if (this.bindingMapGen) {
                if (this.tryBindingMapUpdate(data, changes[0])) {
                    return;
                }
            }
        }
        const dataUpdatePathTree = Object.create(null);
        for (let i = 0; i < changes.length; i += 1) {
            const [p, newVal, spliceIndex, spliceDel] = changes[i];
            let cur = dataUpdatePathTree;
            for (let j = 0; j < p.length; j += 1) {
                const field = p[j];
                const v = cur[field];
                if (v === true)
                    break;
                if (j === p.length - 1) {
                    if (spliceDel === undefined) {
                        // replace update
                        cur[field] = true;
                    }
                    else {
                        // splice update
                        const startIndex = spliceIndex;
                        if (v === undefined) {
                            cur[field] = Object.create(new Array(startIndex));
                        }
                        else if (!Array.isArray(Object.getPrototypeOf(v))) {
                            // if the target is not an array, replace it as an array
                            const arr = new Array(startIndex);
                            const keys = Object.keys(v);
                            let lengthChanged = false;
                            for (let i = 0; i < keys.length; i += 1) {
                                // collect items that have been changed
                                const key = keys[i];
                                const item = v[key];
                                if (isPositiveInteger(key)) {
                                    arr[Number(key)] = item;
                                }
                                else if (key === 'length') {
                                    lengthChanged = true;
                                }
                            }
                            const wrappedArr = Object.create(arr);
                            if (lengthChanged) {
                                wrappedArr.length = true;
                            }
                            cur[field] = wrappedArr;
                        }
                        else {
                            // if the target is already an array, use it
                            const arr = Object.getPrototypeOf(v);
                            if (arr.length < startIndex)
                                arr.length = startIndex;
                            const wrapper = v;
                            const keys = Object.keys(wrapper);
                            for (let i = 0; i < keys.length; i += 1) {
                                // NOTE
                                // Some items may be changed separatedly after the array splice.
                                // These fields should be reassigned to the array,
                                // so that they can be spliced.
                                const key = keys[i];
                                if (isPositiveInteger(key)) {
                                    arr[Number(key)] = wrapper[key];
                                    delete wrapper[key];
                                }
                            }
                        }
                        const arr = Object.getPrototypeOf(cur[field]);
                        const inserts = new Array(newVal.length);
                        inserts.fill(true);
                        arr.splice(spliceIndex, spliceDel, ...inserts);
                    }
                    break;
                }
                if (v === undefined) {
                    const next = Object.create(null);
                    cur[field] = next;
                    cur = next;
                }
                else {
                    cur = v;
                }
            }
        }
        const oldBindingMapGen = this.bindingMapGen;
        const bindingMapGen = (this.bindingMapGen =
            this.procGenWrapper.update(data, dataUpdatePathTree, oldBindingMapGen) || oldBindingMapGen);
        // FIXME: this is a temporary solution for older version of template compiler
        // procGen will return fresh new bindingMapGen each time
        // we need to use the new one but it may contain undefined items
        if (oldBindingMapGen && bindingMapGen && oldBindingMapGen !== bindingMapGen) {
            const keys = Object.keys(bindingMapGen);
            for (let i = 0; i < keys.length; i += 1) {
                const key = keys[i];
                const updators = bindingMapGen[key];
                for (let j = 0; j < updators.length; j += 1) {
                    if (!updators[j])
                        updators[j] = oldBindingMapGen[key][j];
                }
            }
        }
    }
    tryBindingMapUpdate(data, change) {
        if (!change)
            return true;
        const bindingMapGen = this.bindingMapGen;
        if (!bindingMapGen) {
            return false;
        }
        const [path] = change;
        if (path.length !== 1)
            return false;
        return this.procGenWrapper.bindingMapUpdate(path[0], data, bindingMapGen);
    }
}
let defaultTemplateEngine = null;
export const getDefaultTemplateEngine = () => {
    if (defaultTemplateEngine) {
        return defaultTemplateEngine;
    }
    const tmpl = new GlassEaselTemplateEngine();
    defaultTemplateEngine = tmpl;
    return tmpl;
};
//# sourceMappingURL=index.js.map